/** Svelte glue: the real-time loop (pause / 1× / 2× / 3×), autosave, dev hooks.
 *  Sim logic is framework-agnostic; only this module touches Svelte/rAF/DOM. */
import { writable, type Writable } from 'svelte/store';
import type { City, SimAction } from './types';
import { SIM } from './config';
import { reduce } from './reducer';
import { createCity } from './world';
import {
  loadFromStorage,
  saveToStorage,
  exportSave as exportBlob,
  importSave as importBlob,
  clearStorage,
} from './save';

const STEP = SIM.SIM_STEP_SEC;
const MAX_STEPS = 240;

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.timeOrigin + performance.now() : Date.now();
}

export const worldStore: Writable<City> = writable(createCity(nowMs()));
export const now: Writable<number> = writable(nowMs());

let current: City = createCity(nowMs());
let acc = 0;
let lastFrame = nowMs();
let raf = 0;
let running = false;
let autosaveTimer = 0;
let started = false;

function setCity(c: City): void {
  current = c;
  worldStore.set(c);
}

export function dispatch(action: SimAction): void {
  setCity(reduce(current, action, nowMs()));
}
export function getWorld(): City {
  return current;
}

function frame(): void {
  const t = nowMs();
  let real = (t - lastFrame) / 1000;
  lastFrame = t;
  if (real > SIM.MAX_CATCHUP_SEC) real = SIM.MAX_CATCHUP_SEC;

  if (current.speed > 0) {
    acc += real * current.speed;
    let c = current;
    let steps = 0;
    while (acc >= STEP && steps < MAX_STEPS) {
      c = reduce(c, { type: 'tick', dt: STEP }, t);
      acc -= STEP;
      steps++;
    }
    if (steps > 0) setCity(c);
  } else {
    acc = 0;
  }
  now.set(t);
  if (running) raf = requestAnimationFrame(frame);
}

function start(): void {
  if (running) return;
  running = true;
  lastFrame = nowMs();
  acc = 0;
  raf = requestAnimationFrame(frame);
}
function stop(): void {
  running = false;
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
}

function onVisibility(): void {
  if (document.visibilityState === 'hidden') {
    saveToStorage(current);
    stop();
  } else {
    lastFrame = nowMs();
    start();
  }
}

export function exportSave(): string {
  return exportBlob(current);
}
export function importSave(b64: string): boolean {
  const c = importBlob(b64, nowMs());
  if (!c) return false;
  setCity(c);
  saveToStorage(current);
  return true;
}
export function hardReset(): void {
  clearStorage();
  setCity(createCity(nowMs()));
}

export function initSim(): void {
  if (started) return;
  started = true;
  current = loadFromStorage(nowMs()) ?? createCity(nowMs());
  worldStore.set(current);
  now.set(nowMs());

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', () => saveToStorage(current));
  }
  autosaveTimer = setInterval(() => saveToStorage(current), SIM.AUTOSAVE_MS) as unknown as number;
  start();

  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).cn = {
      dispatch,
      getWorld,
      hardReset,
      load: (c: City) => setCity(c),
    };
  }
}

export function teardownSim(): void {
  stop();
  if (autosaveTimer) clearInterval(autosaveTimer);
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
  started = false;
}
